import { DEFAULT_CALENDAR_TASK_DURATION_MINS } from "@lifeat/shared/calendar/CalendarConstants";
import AbstractCalendarItem from "@lifeat/shared/calendar/impl/AbstractCalendarItem";
import CalendarTask from "@lifeat/shared/calendar/impl/CalendarTask";
import { CalendarDetails } from "@lifeat/shared/data-models/calendar/CalendarDetails";
import { Identifiable } from "@lifeat/shared/data-models/definition";
import { CalendarConnection } from "@lifeat/shared/data-models/user/integrations/IntegrationsDataEntity";
import { getErrorMessage } from "@lifeat/shared/error/ErrorUtil";
import { ProjectList } from "@lifeat/shared/projects/ProjectsTypes";
import { Routes } from "@lifeat/shared/system/RouteConstants";
import { TaskItemType } from "@lifeat/shared/task-management/TaskManagementTypes";
import { Tag } from "@lifeat/shared/task-management/TaskTagTypes";
import { Mutex, withMutex } from "@lifeat/shared/util/AsyncUtil";
import { MILLISECONDS_PER_DAY, buildRfc3339Timestamp, getDateFromISODateString } from "@lifeat/shared/util/DateUtil";
import { hideMenubarWindow, showMainWindow } from "@desktop/ToDesktopService";
import { isWindowMenubar } from "@desktop/ToDesktopUtil";
import { logError } from "@service/ConsoleLoggerService";
import { callMonolith } from "@service/ServerlessFunctionsService";
import { buildDeepLink } from "@util/DeepLinkUtil";
import { BrowserUtil } from "@util/index";
import { getTaskTagFromTagArray } from "../task-management/TaskManagementUtil";
import { EVENT_PAGE_SIZE_BY_DAYS } from "./CalendarConstants";

const calendarMutex = new Mutex();

export function getTaskEvents(
    tasks: { [id: string]: TaskItemType },
    tags: Identifiable<Tag>[],
): { [taskId: string]: CalendarTask } {
    const calTasks: { [taskId: string]: CalendarTask } = {};
    if (tasks) {
        for (const taskId in tasks) {
            const task = tasks[taskId];
            if (task?.schedulingDetails) {
                if (task.schedulingDetails.scheduleTime) {
                    const startDate = new Date(task.schedulingDetails.scheduleTime);
                    calTasks[taskId] = CalendarTask.fromTask(
                        taskId,
                        task,
                        startDate,
                        task.schedulingDetails.durationMins ?? DEFAULT_CALENDAR_TASK_DURATION_MINS,
                        getTaskTagFromTagArray(task.tagId, tags),
                    );
                }
                // task due dates will show up as a separate event on the calendar
                if (task.schedulingDetails.dueDate) {
                    const dueDate = getDateFromISODateString(task.schedulingDetails.dueDate);
                    calTasks[`${taskId}-due-date`] = CalendarTask.fromTask(
                        taskId,
                        task,
                        dueDate,
                        task.schedulingDetails.durationMins ?? DEFAULT_CALENDAR_TASK_DURATION_MINS,
                        getTaskTagFromTagArray(task.tagId, tags),
                        true,
                        true,
                    );
                }
            }
        }
    }
    return calTasks;
}

export function getProjectDeadlineEvents(projectList: ProjectList) {
    const calTasks: { [projectId: string]: CalendarTask } = {};

    for (const projectId in projectList) {
        const project = projectList[projectId];
        if (project?.endDate) {
            const endDate = getDateFromISODateString(project.endDate);
            calTasks[projectId] = CalendarTask.fromProject(projectId, project, endDate);
        }
    }

    return calTasks;
}

export const fetchEventsForDateRange = withMutex(
    calendarMutex,
    async (newDateTime: number): Promise<AbstractCalendarItem[]> => {
        try {
            const res = await callMonolith("get-calendar-events", {
                maximumStartTime: buildRfc3339Timestamp(
                    newDateTime + EVENT_PAGE_SIZE_BY_DAYS.NextPage * MILLISECONDS_PER_DAY,
                ),
                minimumEndTime: buildRfc3339Timestamp(
                    newDateTime - EVENT_PAGE_SIZE_BY_DAYS.PrevPage * MILLISECONDS_PER_DAY,
                ),
            });

            for (const warning of res.warnings) {
                logError(warning);
            }

            return res.eventItems;
        } catch (e) {
            const errMsg = getErrorMessage(e);
            logError(
                `Could not fetch events for calendars. Please ensure LifeAt has ` +
                    `permission to read your events.\n[Error: ${errMsg}]`,
            );
            throw new Error("Could not fetch events for calendars. Error: " + errMsg);
        }
    },
);

export const fetchCalendarLists = withMutex(calendarMutex, async () => {
    return await callMonolith("get-calendar-list", undefined);
});

export const updateSelectedCalendarList = withMutex(
    calendarMutex,
    async (data: { calendar: CalendarDetails; selected: boolean }) => {
        await callMonolith("calendar-update-selected", data);
    },
);

export const removeCalendarConnection = withMutex(calendarMutex, async (connection: CalendarConnection) => {
    await callMonolith("calendar-remove", { connection });
});

/**
 * Handle joining a calendar event meeting link.
 * @param meetingLink
 * @param history react-router-dom history from useHistory
 */
export const joinEventMeetingLink = (meetingLink: string, history: any) => {
    if (meetingLink) {
        // For joining a LifeAt Room from within the desktop app, we don't want to
        // redirect the user via the browser. Instead we replace their location.
        if (window.todesktop && _isMeetingLinkLifeatRoom(meetingLink)) {
            const splitLink = meetingLink.split("/room/");
            const roomIdentifier = splitLink[1];
            const newRoute = `/${Routes.RoomAccess}/${roomIdentifier}`;

            if (isWindowMenubar()) {
                hideMenubarWindow().then(() => {
                    showMainWindow(`${buildDeepLink(newRoute)}`).then(() => {});
                });
            } else {
                history.push(newRoute);
            }
        } else {
            BrowserUtil.openLinkNewTab(meetingLink);
        }
    }
};

function _isMeetingLinkLifeatRoom(meetingLink: string): boolean {
    return meetingLink.includes("lifeat") && meetingLink.includes("/room/");
}
